import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Badge, Button, Heading, Skeleton, Spinner, StatCard, Text } from "@lendcord/concord";
import {
  useApproveApplicationMutation,
  useCheckCrifMutation,
  useGetCrifReportQuery,
  useLazyGetCrifDownloadQuery,
} from "@/features/onboarding/api/onboarding.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { ScoreGauge } from "./ScoreGauge";
import type { CrifCheckResult, CrifDecision } from "@/features/onboarding/onboarding.types";

interface CrifStepProps {
  applicationId: string;
  isEditAllowed: boolean;
}

const RETRY_DELAY_MS = 5000;
const MAX_AUTO_RETRIES = 5;

const DECISION_BADGE_STATUS: Record<CrifDecision, "success" | "warning" | "error"> = {
  APPROVE: "success",
  REFER: "warning",
  REJECT: "error",
};

const DECISION_LABEL_ID: Record<CrifDecision, string> = {
  APPROVE: "lender.onboarding.review.crifDecisionApprove",
  REFER: "lender.onboarding.review.crifDecisionRefer",
  REJECT: "lender.onboarding.review.crifDecisionReject",
};

// Form 3 — the CRIF credit check. Unlike VerificationStep/LoanTermsStep, this
// isn't a form: there's no payload to collect, the backend already has
// everything it needs from the applicant's own profile (see
// ApplicationReviewService.checkCrif) — the only input is the trigger itself.
// Rendered for both LOAN_DETAILS_VERIFICATION (not yet run) and
// CIBIL_VERIFICATION (run at least once) — GET .../crif/report recalls
// whatever the last successful check produced so a reviewer revisiting this
// application later sees it without re-running anything.
export function CrifStep({ applicationId, isEditAllowed }: CrifStepProps) {
  const intl = useIntl();
  const { data: existingReport, isLoading: isLoadingReport, error: reportError } = useGetCrifReportQuery(applicationId);
  const [checkCrif, { isLoading: isChecking }] = useCheckCrifMutation();
  const [fetchDownload, { isFetching: isDownloading }] = useLazyGetCrifDownloadQuery();
  const [approveApplication, { isLoading: isApproving }] = useApproveApplicationMutation();

  const [result, setResult] = useState<CrifCheckResult | null>(null);
  const [checkError, setCheckError] = useState<string | undefined>();
  const [approveError, setApproveError] = useState<string | undefined>();
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Seed local state from the recalled report once it loads — a 404
  // (LEND-031) just means "never completed", not an error.
  useEffect(() => {
    if (existingReport) setResult(existingReport);
  }, [existingReport]);

  const hasNeverRun = !existingReport && isQueryApiError(reportError) && reportError.code === "LEND-031";

  async function runCheck() {
    setCheckError(undefined);
    try {
      const response = await checkCrif(applicationId).unwrap();
      setResult(response);
      if (response.crifStatus !== "PENDING") {
        retryCountRef.current = 0;
      }
    } catch (error) {
      setCheckError(
        isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.onboarding.review.crifCheckError" }),
      );
    }
  }

  // PENDING is transient (the provider call itself failed) — auto-retry a
  // few times before leaving it to a manual "Check Again", mirroring the
  // backend's own "5s poll, re-call the same endpoint" note.
  useEffect(() => {
    if (result?.crifStatus !== "PENDING" || retryCountRef.current >= MAX_AUTO_RETRIES) {
      return;
    }
    retryCountRef.current += 1;
    retryTimeoutRef.current = setTimeout(runCheck, RETRY_DELAY_MS);
    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.crifStatus]);

  async function handleDownloadPdf() {
    const { pdfUrl } = await fetchDownload(applicationId).unwrap();
    if (pdfUrl) window.open(pdfUrl, "_blank", "noopener,noreferrer");
  }

  async function handleApprove() {
    setApproveError(undefined);
    try {
      await approveApplication(applicationId).unwrap();
    } catch (error) {
      setApproveError(
        isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.onboarding.review.crifApproveError" }),
      );
    }
  }

  if (isLoadingReport) {
    return (
      <div className="mt-6 flex flex-col gap-4 rounded-md border border-border bg-surface-raised p-6">
        <Skeleton variant="text" className="h-5 w-40" />
        <Skeleton variant="rect" className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="mt-6 flex flex-col gap-5 rounded-md border border-border bg-surface-raised p-6">
      <div>
        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.onboarding.review.crifHeading" />
        </Heading>
        <Text as="p" size="sm" variant="muted" className="mt-1">
          <FormattedMessage id="lender.onboarding.review.crifDescription" />
        </Text>
      </div>

      {checkError && <AlertBanner status="error">{checkError}</AlertBanner>}

      {(hasNeverRun || !result) && (
        <div>
          <Button type="button" onClick={runCheck} disabled={isChecking}>
            {isChecking ? (
              <FormattedMessage id="lender.onboarding.review.crifRunningButton" />
            ) : (
              <FormattedMessage id="lender.onboarding.review.crifRunButton" />
            )}
          </Button>
        </div>
      )}

      {result?.crifStatus === "PENDING" && (
        <AlertBanner status="warning">
          <div className="flex items-center gap-2">
            <Spinner className="h-4 w-4" />
            <span>
              {result.message ?? intl.formatMessage({ id: "lender.onboarding.review.crifPendingMessage" })}
            </span>
          </div>
        </AlertBanner>
      )}

      {result?.crifStatus === "PENDING" && retryCountRef.current >= MAX_AUTO_RETRIES && (
        <div>
          <Button type="button" variant="secondary" onClick={runCheck} disabled={isChecking}>
            <FormattedMessage id="lender.onboarding.review.crifRetryButton" />
          </Button>
        </div>
      )}

      {result?.crifStatus === "FAILED" && (
        <AlertBanner status="error">
          {result.message ?? intl.formatMessage({ id: "lender.onboarding.review.crifFailedMessage" })}
        </AlertBanner>
      )}

      {result?.crifStatus === "COMPLETE" && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {result.score != null ? (
              <div className="flex items-center justify-center rounded-md border border-border p-4">
                <ScoreGauge score={result.score} bandLabel={result.scoreBand} />
              </div>
            ) : (
              <StatCard label={intl.formatMessage({ id: "lender.onboarding.review.crifScoreLabel" })} value="—" />
            )}
            <div className="flex flex-col justify-center gap-2 rounded-md border border-border p-6">
              <Text as="p" size="sm" variant="muted">
                <FormattedMessage id="lender.onboarding.review.crifDecisionLabel" />
              </Text>
              {result.analysis ? (
                <div>
                  <Badge status={DECISION_BADGE_STATUS[result.analysis.decision]}>
                    <FormattedMessage id={DECISION_LABEL_ID[result.analysis.decision]} />
                  </Badge>
                </div>
              ) : (
                <Text size="sm">—</Text>
              )}
            </div>
          </div>

          {approveError && <AlertBanner status="error">{approveError}</AlertBanner>}

          {result.analysis && result.analysis.reasons.length > 0 && (
            <div>
              <Text as="p" size="sm" className="font-medium">
                <FormattedMessage id="lender.onboarding.review.crifReasonsHeading" />
              </Text>
              <ul className="mt-2 list-inside list-disc">
                {result.analysis.reasons.map((reason) => (
                  <li key={reason}>
                    <Text as="span" size="sm" variant="muted">
                      {reason}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            {result.download?.pdfUrl && (
              <Button type="button" variant="secondary" onClick={handleDownloadPdf} disabled={isDownloading}>
                <FormattedMessage id="lender.onboarding.review.crifDownloadPdfButton" />
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={runCheck} disabled={isChecking}>
              <FormattedMessage id="lender.onboarding.review.crifRerunButton" />
            </Button>
            {isEditAllowed && (
              <Button type="button" onClick={handleApprove} disabled={isApproving}>
                {isApproving ? (
                  <FormattedMessage id="lender.onboarding.review.crifApprovingButton" />
                ) : (
                  <FormattedMessage id="lender.onboarding.review.crifApproveButton" />
                )}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
